import { useInfiniteQuery } from '@tanstack/react-query';
import {
  dexBotKeys,
  DexBotService,
} from 'api/services/httpServices/DexBotService';
import { BotType, GetDexBotsParams } from 'api/types/httpsTypes/dex-bot';
import { ErrorPageDex } from '../../research/components/shared/Common';
import React, { useEffect, useState } from 'react';
import {
  useBotsSearchFilters,
  useWhitelistedTokensSearchFilters,
  WhitelistedTokensFilterState,
} from './usePageParams';
import { useUserState } from 'modules/user/UserContext';
import Button from 'modules/shared-components/button/button';
import {
  ChartBarSquareIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  Square3Stack3DIcon,
} from '@heroicons/react/24/outline';
import { WhitelistedTokensTable } from './whitelistedTokens/WhitelistedTokensTable';
import AddNewTokenModal from './whitelistedTokens/AddNewTokenModal';
import { useDebouncedState } from 'utils/useDebounce';
import { DexPanelButton } from '../../components/tabs';
import TextInput from '../../components/inputs/text-input/text-input';
import { BotsMainTable, thDynamicClass } from './BotsTable';
import Table from 'modules/shared-components/data-display/table';
import Header from 'modules/shared-components/data-display/table/header';
import { desktopColumns, mobileColumns } from './columns';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import { Chains } from 'api/types/httpsTypes/d-wallets';
import { FullPageNotificationDex } from '../../fullPageNotification';
import * as yup from 'yup';
import { useQueryParams } from 'modules/routing/useQueryParams';
import { useInView } from 'react-intersection-observer';
import { useChainPersistedState } from '../../components/chains/getChains';

const schema = {
  tab: yup
    .string()
    .default('bot-manager')
    .oneOf(['bot-manager', 'token-manager']),
  whitelistChain: yup.string().oneOf(Object.values(Chains)),
  whitelistAddress: yup.string(),
};

export function usePageParams() {
  return useQueryParams(schema);
}

export function BotCard({ botType }: { botType: BotType }) {
  const [{ tab: botTab, whitelistChain, whitelistAddress }, setBotTab] =
    usePageParams();
  const [address, debouncedAddress, setAddress] = useDebouncedState('', 250);
  const { filtersState: filters, setFilters } =
    useWhitelistedTokensSearchFilters();

  const updateFilters = (update: Partial<WhitelistedTokensFilterState>) => {
    setFilters({ ...filters, ...update });
  };

  useEffect(() => {
    if (debouncedAddress) {
      updateFilters({
        whitelistedTokensAddress: debouncedAddress,
      });
    }
  }, [debouncedAddress]);

  const { filtersState } = useBotsSearchFilters();
  const { user } = useUserState();

  const [selectedChain, setSelectedChain] = useChainPersistedState(
    Chains.Solana
  );

  useEffect(() => {
    if (whitelistChain) {
      setSelectedChain(whitelistChain);
    }
  }, [whitelistChain]);

  const [isAddTokenOpen, setIsAddTokenOpen] = useState(
    !!(whitelistChain && whitelistAddress)
  );
  const isLargeScreen = useMediaQuery(screenGte.large);

  const columns = isLargeScreen
    ? desktopColumns({ botType })
    : mobileColumns({ botType });

  const params: GetDexBotsParams = {
    status: ['ACTIVE', 'PAUSED'],
    sort: filtersState.botSort,
    chain: selectedChain,
    botType,
  };

  const {
    data,
    error,
    isInitialLoading: isLoading,
    fetchNextPage,
  } = useInfiniteQuery({
    queryFn: ({ pageParam = { limit: 10, offset: 0 } }) => {
      return DexBotService.getBots({
        ...params,
        limit: pageParam.limit,
        offset: pageParam.offset,
      });
    },
    queryKey: dexBotKeys.getBots(params),
    getNextPageParam: (lastPage) => ({
      offset: lastPage.pagination.offset + lastPage.pagination.limit,
      limit: 10,
    }),
    keepPreviousData: true,
    enabled: !!user,
  });

  const items = data?.pages?.flatMap((page) => page.data) || [];
  const pagination = data?.pages?.[0]?.pagination;

  const handleTokenAddressInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();

    const inputValue = e.target.value;
    setAddress(inputValue);
    if (!inputValue) {
      updateFilters({
        whitelistedTokensAddress: undefined,
      });
    }
  };

  const BotTokensActions = () => {
    return (
      <div className="xxs:px-2 xxs:pb-2 lg:pb-0 lg:px-0 flex lg:flex-row-reverse xxs:space-x-2 lg:gap-x-1 items-center xxs:justify-between lg:justify-end grow">
        <div className="lg:flex xxs:w-full lg:w-auto">
          <TextInput
            name="WalletAddress"
            value={address}
            prefix={
              <MagnifyingGlassIcon className="w-4 xxs:text-dex-white-secondary xxs:my-1" />
            }
            onChange={handleTokenAddressInputChange}
            placeholder="Search Address"
          />
        </div>
        <Button
          size="small"
          type="button"
          onClick={() => setIsAddTokenOpen(true)}
          variant="dexNeutral"
        >
          <div className="flex space-x-1 items-center xxs:py-2 lg:py-1">
            <PlusIcon className="w-4 h-4" />
            <div className="hidden lg:block normal-case	text-nowrap">
              Add Token
            </div>
          </div>
        </Button>
        {isAddTokenOpen && (
          <AddNewTokenModal
            tA={whitelistAddress}
            handleClose={() => setIsAddTokenOpen(false)}
          />
        )}
      </div>
    );
  };
  const BotTabs = () => {
    return (
      <div className="flex flex-col xxs:space-y-2 lg:space-y-0">
        <div className="flex xxs:gap-2 lg:gap-1 lg:pb-1 items-center xxs:px-2 lg:px-0">
          <DexPanelButton
            type="button"
            className="xxs:w-full lg:w-auto xxs:p-2 xxs:rounded-none"
            onClick={() => setBotTab({ tab: 'bot-manager' })}
            isActive={botTab === 'bot-manager'}
          >
            <div className="flex space-x-1 items-center justify-center">
              <ChartBarSquareIcon className="h-4 w-4" />
              <div>Bots Manager</div>
            </div>
          </DexPanelButton>
          <div className="xxs:w-full lg:w-auto lg:flex lg:grow lg:justify-between">
            <DexPanelButton
              className="xxs:w-full lg:w-auto  xxs:p-2 xxs:rounded-b-none xxs:rounded-r-none"
              onClick={() => setBotTab({ tab: 'token-manager' })}
              isActive={botTab === 'token-manager'}
            >
              <div className="flex space-x-1 items-center justify-center">
                <Square3Stack3DIcon className="h-4 w-4" />
                <div>Tokens Manager</div>
              </div>
            </DexPanelButton>
            {botTab === 'token-manager' && (
              <div className="hidden lg:block">
                <BotTokensActions />
              </div>
            )}
          </div>
        </div>
        {botTab === 'token-manager' && (
          <div className="lg:hidden">
            <BotTokensActions />
          </div>
        )}
      </div>
    );
  };

  const { ref } = useInView({
    onChange: (inView) => {
      if (inView) {
        void fetchNextPage();
      }
    },
  });

  return (
    <div className="h-full">
      {!user ? (
        <FullPageNotificationDex
          height="100px"
          title="You're not logged in!"
          subTitle="To create a new bot, please log in."
          href="/login"
          hrefTitle="Log In"
        />
      ) : error ? (
        <ErrorPageDex height="h-[500px]" />
      ) : isLoading ? (
        <div>
          <div>
            <div className="w-full lg:min-w-[1000px] ">
              <Table className="xxs:[&_td:first-of-type:not(.details)]:pl-2 xxs:[&_th:first-of-type]:pl-2 lg:[&_td:first-of-type:not(.details)]:pl-6 lg:[&_th:first-of-type]:pl-6">
                <Header
                  trClassName="lg:grid lg:grid-cols-11 lg:dark:bg-dex-black-800  border-tailwind xxs:border-x-0 xxs:border-t-0 xxs:border-b-dex-black-700"
                  columns={columns}
                  thDynamicClass={thDynamicClass}
                  rowClassName="xxs:p-1"
                  sortBy={filtersState.botSort?.replace('-', '')}
                  className="lg:dark:bg-dex-black-700 xxs:text-xs lg:grid"
                />
                <tbody className="lg:block lg:h-terminal overflow-y-auto">
                  {[...Array(10).keys()]
                    .map((i) => i + 1)
                    .map((index) => (
                      <Table.LoadingRow
                        key={index}
                        cellClassName="xxs:py-3"
                        elementClassName="xxs:dark:bg-dex-black-600"
                        thDynamicClass={thDynamicClass}
                        className="lg:grid lg:grid-cols-11 lg:dark:bg-dex-black-800"
                        columns={columns}
                      />
                    ))}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex flex-col justify-between xxs:min-h-[600px] lg:min-h-[500px] h-full space-y-2">
            {items && (
              <div className="overflow-x-auto xxs:space-y-2 lg:space-y-0">
                {botType === BotType.Volatility && items?.length > 0 && (
                  <BotTabs />
                )}
                {botTab === 'token-manager' &&
                botType === BotType.Volatility ? (
                  <WhitelistedTokensTable />
                ) : (
                  <BotsMainTable
                    ref={ref}
                    items={items}
                    pagination={pagination}
                    botType={botType}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
